import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Menu, Transition, Disclosure } from "@headlessui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { Fragment } from "react";
import { ChevronDown, ChevronUp, ChevronRight, Play, X, Settings, Wand2, Image, Clock, Wind, Smile, Brackets, Heart, Check, Megaphone, Pause, Loader2, Globe, Lightbulb, Sparkles, RefreshCw, Video, Zap, ChevronsRight, Laugh, SmilePlus, VolumeX, CircleDot } from "lucide-react";
import services from "../../../API";
import { useSelector } from "react-redux";
import { Loading } from "../../Loader/Loading";
import axios from "axios";
import VoiceLibrarySidebar from "../../AiModule/VoiceLibrarySidebar";
import SpeechWaveformPlayer from "../../AiModule/SpeechWaveformPlayer";
import ScriptHistoryControls from "../../AiModule/ScriptHistoryControls";
import useScriptHistory from "../../AiModule/useScriptHistory";
import GenerationHistorySidebar from "../../AiModule/GenerationHistorySidebar";
import AdAssetLibraryModal from "../../VideoEditor/components/AdAssetLibraryModal";
import { ExtraCreditsModal } from "../../PricingPlans/Plans/ExtraCredits";
import { toastify } from "../../Toast";

// --- Helpers & Mock Data (Consistent with SpeakingCharacter.js) ---



const TripleChevronsRight = ({ className = "" }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="m1 5 3 3-3 3" />
    <path d="m6 5 3 3-3 3" />
    <path d="m11 5 3 3-3 3" />
  </svg>
);

const voiceCues = [
  { icon: <Zap className="mr-2 h-4 w-4" />, label: "[fast]", value: "[fast]" },
  { icon: <ChevronsRight className="mr-2 h-4 w-4" />, label: "[faster]", value: "[faster]" },
  { icon: <TripleChevronsRight className="mr-2 h-4 w-5" />, label: "[fastest]", value: "[fastest]" },
  { icon: <Clock className="mr-2 h-4 w-4" />, label: "[slow]", value: "[slow]" },
  { icon: <Pause className="mr-2 h-4 w-4" />, label: "[pause]", value: "[pause]" },
  { icon: <Laugh className="mr-2 h-4 w-4" />, label: "[laugh]", value: "[laugh]" },
  { icon: <Smile className="mr-2 h-4 w-4" />, label: "[chuckle]", value: "[chuckle]" },
  { icon: <SmilePlus className="mr-2 h-4 w-4" />, label: "[giggles]", value: "[giggles]" },
  { icon: <Wind className="mr-2 h-4 w-4" />, label: "[sigh]", value: "[sigh]" },
  { icon: <VolumeX className="mr-2 h-4 w-4" />, label: "[whisper]", value: "[whisper]" },
  { icon: <CircleDot className="mr-2 h-4 w-4" />, label: "[breath]", value: "[breath]" },
  { icon: <Megaphone className="mr-2 h-4 w-4" />, label: "[emphasis]", value: "[emphasis]" },
];

const mergeUniqueVoices = (...voiceGroups) => {
  const seen = new Set();
  return voiceGroups.flat().filter((voice) => {
    const voiceId = voice?.voiceId;
    if (!voiceId || seen.has(voiceId)) return false;
    seen.add(voiceId);
    return true;
  });
};

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return value;
  return value._id || value.id || value.voiceId || value.variantId || "";
};

const buildVoiceSuggestPayload = ({
  gender = "",
  language = "",
  age = "",
  accent = "",
  category = "",
  search = "",
  page = 1,
  pageSize = 50,
}) => {
  const payload = { page, pageSize };
  if (gender && gender !== "all") payload.gender = gender;
  if (language) {
    payload.language = language.startsWith("en") ? "en" : language;
    if (language.startsWith("en")) {
      payload.locale = language === "en-uk" ? "en-GB" : "en-US";
    }
  }
  if (age && age !== "all") {
    payload.age = age;
  }

  if (accent && accent !== "all") payload.accent = accent;
  if (category && category !== "all") payload.category = category;
  if (search) payload.search = search;
  return payload;
};

const voiceStabilityOptions = [
  { label: "Expressive", description: ["More emotion, variation,", "and personality."], value: 0 },
  { label: "Balanced", description: ["Natural expression with", "consistent delivery."], value: 0.5 },
  { label: "Consistent", description: ["Steadier tone, pacing,", "and delivery."], value: 1 },
];

const normalizeVoiceStability = (value) => {
  const numericValue = Number(Array.isArray(value) ? value[0] : value);

  if (!Number.isFinite(numericValue)) return 0.5;

  return voiceStabilityOptions.reduce((closest, option) =>
    Math.abs(option.value - numericValue) < Math.abs(closest - numericValue)
      ? option.value
      : closest
    , 0.5);
};

const VoiceStabilityControl = ({ value, onChange }) => {
  const selectedOption = voiceStabilityOptions.find((option) => option.value === value);

  return (
    <div>
      <div className="grid grid-cols-3 gap-1.5 sm:gap-3">
        {voiceStabilityOptions.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              aria-pressed={isSelected}
              className="flex min-w-0 flex-col items-center text-center"
            >
              <span className={`w-full rounded-lg px-0.5 sm:px-2 py-1.5 text-[11px] sm:text-sm font-semibold transition-colors ${isSelected
                ? "bg-gray-900 text-white shadow-sm dark:bg-gray-100 dark:text-gray-900"
                : "text-gray-900 dark:text-gray-100"
                }`}>
                {option.label}
              </span>
              <span className="mt-2 w-full text-[11px] sm:text-xs leading-4 text-gray-500 dark:text-gray-400">
                {option.description.join(" ")}
              </span>
            </button>
          );
        })}
      </div>

      <div className="relative mt-4 h-4">
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div
          className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-orange-500 transition-all"
          style={{ width: `${value * 100}%` }}
        />
        <input
          type="range"
          min="0"
          max="1"
          step="0.5"
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          aria-label="Voice stability"
          aria-valuetext={selectedOption?.label}
          className="absolute inset-0 z-10 h-4 w-full cursor-pointer appearance-none border-0 bg-transparent outline-none focus:border-0 focus:outline-none focus:ring-0 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-orange-500 [&::-moz-range-track]:h-2 [&::-moz-range-track]:border-0 [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:-mt-1 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-orange-500"
        />
      </div>
    </div>
  );
};

// --- Component Definition ---

export function SpeakingCharacterStudio({ embedded, projectName, onBack, onGenerate, initialScript, aiCmoId, conceptId }) {
  const navigate = useNavigate();
  const location = useLocation();
  useRef(null);
  const audioRefGen = useRef(null);
  if (!audioRefGen.current) audioRefGen.current = new Audio();
  const previewAudioRef = useRef(null);
  if (!previewAudioRef.current) previewAudioRef.current = new Audio();

   // Premium design defaults to dark mode

  // State Management
  const [selectedLanguage, setSelectedLanguage] = useState("en-us");
  const [libraryCharacter, setLibraryCharacter] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState();
  const [prioritizeReturnedCharacter, setPrioritizeReturnedCharacter] = useState(
    () => location.state?.characterSelectionSource === "all-characters"
  );
  const {
    script, setScript, addGeneratedScript, currentScriptNumber, scriptCount,
    goToPreviousScript, goToNextScript,
  } = useScriptHistory(initialScript || "");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [librarySelectedVoiceDetails, setLibrarySelectedVoiceDetails] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [characterImage, setCharacterImage] = useState(null);

  // Voice Settings
  const [voiceSpeed, setVoiceSpeed] = useState(1);
  const [voiceStability, setVoiceStability] = useState(0.5);
  const [voiceTone, ] = useState("Professional");
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);

  // Voice & History State
  const [accumulatedVoices, setAccumulatedVoices] = useState([]);
  const [searchedVoices, ] = useState([]);
  useState([]);
  const [voicePage, setVoicePage] = useState(1);
  const [hasMoreVoices, setHasMoreVoices] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSyncingVoices, setIsSyncingVoices] = useState(false);
  const [voiceSearchQuery, setVoiceSearchQuery] = useState("");
  const [voiceFilterGender, setVoiceFilterGender] = useState("all");
  const [voiceFilterAge, setVoiceFilterAge] = useState("all");
  const [voiceFilterCategory, setVoiceFilterCategory] = useState("all");
  const [showVoiceLibrary, setShowVoiceLibrary] = useState(false);
  const [showGenerationHistory, setShowGenerationHistory] = useState(false);
  const [showExtraCredits, setShowExtraCredits] = useState(false);
  const [historyImages, setHistoryImages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedGenerationImage, setSelectedGenerationImage] = useState(null);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);

  // Audio Playback
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioInProcess, setAudioInProcess] = useState(false);
  const audioRequestRef = useRef(null);


  useEffect(() => {
    setAudioInProcess(false);
    return () => {
      audioRequestRef.current?.abort();
      audioRequestRef.current = null;
    };
  }, [selectedVoice]);
  const [previewingVoiceId, setPreviewingVoiceId] = useState(null);

  // Generation Process
  const [isProcessing, setIsProcessing] = useState(false);
  const previewPollIntervalRef = useRef(null);
  const previewRequestTokenRef = useRef(0);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [showScriptPrompt, ] = useState(false);
  const [scriptPrompt, setScriptPrompt] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);

  const scriptTextareaRef = useRef(null);
  const voiceSettingsRef = useRef(null);
  const voiceSettingsBtnRef = useRef(null);

  useEffect(() => {
    return () => {
      previewRequestTokenRef.current += 1;
      if (previewPollIntervalRef.current) {
        clearInterval(previewPollIntervalRef.current);
        previewPollIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showVoiceSettings &&
        voiceSettingsRef.current &&
        !voiceSettingsRef.current.contains(event.target) &&
        voiceSettingsBtnRef.current &&
        !voiceSettingsBtnRef.current.contains(event.target)
      ) {
        setShowVoiceSettings(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showVoiceSettings]);

  // Redux Selectors
  const allCharacters = useSelector((state) => state.data.apiData.characters?.data || []);
  const loading = useSelector((state) => state.data.loading.characters);
  const loadingVoices = useSelector((state) => state.data.loading.studioVoices);
  const loadingVideo = useSelector((state) => state.data.loading.generateVideo);
  const voicesDataFromRedux = useSelector((state) => state.data.apiData.studioVoices?.data?.suggestedVoices || []);
  const selectedCharacterGender = (
    allCharacters.find((character) => character?._id === selectedCharacter)?.attributes?.gender ||
    allCharacters.find((character) => character?._id === selectedCharacter)?.gender ||
    ""
  ).toLowerCase();

  // Restore generated audio after returning from the character library. The
  // previous component revokes its object URL on unmount, so create a fresh
  // URL from the preserved blob when the character gender is compatible.
  useEffect(() => {
    const state = location.state;
    if (!state || state.regenerationData || state.selectedCharacterId === undefined) return;

    const returnedCharacterId = typeof state.selectedCharacterId === "object"
      ? state.selectedCharacterId?._id || state.selectedCharacterId?.id
      : state.selectedCharacterId;
    const returnedCharacter = allCharacters.find((character) => character?._id === returnedCharacterId);
    const returnedCharacterGender = getCharacterGender(returnedCharacter);
    const hasGenderChanged = state.sourceCharacterGender && returnedCharacterGender
      ? state.sourceCharacterGender !== returnedCharacterGender
      : false;

    if (state.sourceCharacterGender && !returnedCharacterGender) return;
    if (hasGenderChanged) {
      audioRefGen.current.pause();
      audioRefGen.current.removeAttribute("src");
      audioRefGen.current.load();
      setSelectedVoice(null);
      setAudioBlob(null);
      setAudioUrl(null);
      return;
    }

    if (state.audioBlob) {
      const restoredUrl = URL.createObjectURL(state.audioBlob);
      setAudioBlob(state.audioBlob);
      setAudioUrl(restoredUrl);
      audioRefGen.current.src = restoredUrl;
      audioRefGen.current.load();
      return;
    }

    if (state.audioUrl) {
      setAudioUrl(state.audioUrl);
      audioRefGen.current.src = state.audioUrl;
      audioRefGen.current.load();
      fetch(state.audioUrl)
        .then((response) => response.blob())
        .then((blob) => setAudioBlob(blob))
        .catch((error) => console.error("Error restoring generated audio:", error));
    }
  }, [location.state, allCharacters]);

  const [favoritedCarouselCharacters, setFavoritedCarouselCharacters] = useState(() => {
    if (typeof window !== "undefined") {
      const numeric = JSON.parse(localStorage.getItem("favoriteCharacters") || "[]");
      const custom = JSON.parse(localStorage.getItem("favoriteCustomCharacters") || "[]");
      return [...numeric, ...custom];
    }
    return [];
  });

  // --- Effects ---

  // Scroll to top on mount / navigation
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.state]);

  useEffect(() => {
    services.getCharacters();
  }, []);

  const currentChar = allCharacters.find((c) => c?._id === selectedCharacter);
  const handledSelectionKey = useRef(null);
  const handleCharacterSelect = useCallback((character, { preserveAudio = false, prioritizeCharacter = false } = {}) => {
    if (!character) return;
    previewRequestTokenRef.current += 1;
    if (previewPollIntervalRef.current) {
      clearInterval(previewPollIntervalRef.current);
      previewPollIntervalRef.current = null;
    }
    setIsProcessing(false);
    setPrioritizeReturnedCharacter(prioritizeCharacter);
    setSelectedCharacter(character?._id);
    setLibraryCharacter(null);
    setCharacterImage(null);
    setSelectedGenerationImage(null);

    const charGender = getCharacterGender(character);
    const charLang = getCharacterLanguage(character);
    const shouldRefreshVoices = Boolean(
      charGender && getCharacterGender(currentChar) && charGender !== getCharacterGender(currentChar)
    );
    const shouldKeepVoiceAndAudio = preserveAudio || !shouldRefreshVoices;

    if (!shouldKeepVoiceAndAudio) {
      setSelectedVoice(null);
      setAudioUrl(null);
      setAudioBlob(null);
    }
    if (charGender && shouldRefreshVoices) setVoiceFilterGender(charGender);
    if (charLang && shouldRefreshVoices) setSelectedLanguage(charLang);

    if (shouldRefreshVoices) {
      setVoicePage(1);
      setAccumulatedVoices([]);
      setHasMoreVoices(true);
      setIsSyncingVoices(true);
    }
  }, [currentChar]);

  useEffect(() => {
    if (location.state?.selectedCharacterId && allCharacters.length > 0) {
      const selectionKey = `${location.key}:${location.state.selectedCharacterId}`;
      if (handledSelectionKey.current === selectionKey) return;
      const character = allCharacters.find(c => c?._id === location.state.selectedCharacterId);
      if (character) {
        const state = location.state;
        const characterGender = getCharacterGender(character);
        const hasGenderChanged = state.sourceCharacterGender && characterGender
          ? state.sourceCharacterGender !== characterGender
          : false;
        const preserveAudio = !hasGenderChanged;

        handledSelectionKey.current = selectionKey;
        handleCharacterSelect(character, {
          preserveAudio,
          prioritizeCharacter: state.characterSelectionSource === "all-characters",
        });

        // Restore other state if present
        if (state.script !== undefined) setScript(state.script);
        if (preserveAudio && state.selectedVoice !== undefined) setSelectedVoice(state.selectedVoice);
        if (state.selectedLanguage !== undefined) setSelectedLanguage(state.selectedLanguage);
        if (state.stability !== undefined) setVoiceStability(normalizeVoiceStability(state.stability));
        if (state.speed !== undefined) setVoiceSpeed(state.speed);
        if (state.customPrompt !== undefined) setCustomPrompt(state.customPrompt);

        // Clear the state to prevent re-triggering if the component re-renders
        navigate(location.pathname, {
          replace: true,
          state: { ...location.state, selectedCharacterId: undefined }
        });
      }
    }
  }, [location.state, allCharacters, navigate, location.pathname, location.key, handleCharacterSelect, setScript]);

  useEffect(() => {
    if (initialScript) setScript(initialScript);
  }, [initialScript, setScript]);

  // Auto-expand textarea whenever script changes
  useEffect(() => {
    if (scriptTextareaRef.current) {
      scriptTextareaRef.current.style.height = "auto";
      scriptTextareaRef.current.style.height = scriptTextareaRef.current.scrollHeight + "px";
    }
  }, [script]);

  // Sync voices when filters change
  useEffect(() => {
    const triggerFetch = () => {
      setIsSyncingVoices(false);
      setVoicePage(1);
      setAccumulatedVoices([]);
      setHasMoreVoices(true);

      const genderFilter = voiceFilterGender === "all" ? "" : voiceFilterGender;
      const ageFilter = voiceFilterAge === "all" ? "" : voiceFilterAge;
      const categoryFilter = voiceFilterCategory === "all" ? "" : voiceFilterCategory;

      services.studioVoices(
        buildVoiceSuggestPayload({
          gender: genderFilter || selectedCharacterGender,
          language: selectedLanguage,
          age: ageFilter,
          accent: "",
          category: categoryFilter,
          search: voiceSearchQuery,
          page: 1,
          pageSize: 50,
        })
      );
    };

    const delay = voiceSearchQuery ? 400 : 0;
    const delayDebounce = setTimeout(triggerFetch, delay);
    return () => clearTimeout(delayDebounce);
  }, [selectedCharacterGender, selectedLanguage, voiceFilterGender, voiceFilterAge, voiceFilterCategory, voiceSearchQuery]);

  useEffect(() => {
    if (loadingVoices) return;
    if (voicesDataFromRedux) {
      if (voicePage === 1) {
        setAccumulatedVoices(voicesDataFromRedux);
      } else if (voicesDataFromRedux.length > 0) {
        setAccumulatedVoices(prev => {
          const existingIds = new Set(prev.map(v => v.voiceId));
          const newVoices = voicesDataFromRedux.filter(v => !existingIds.has(v.voiceId));
          return [...prev, ...newVoices];
        });
      }
      if (voicesDataFromRedux.length < 50) setHasMoreVoices(false);
      setIsLoadingMore(false);
    }
  }, [voicesDataFromRedux, voicePage, loadingVoices]);

  // Audio Cleanup
  useEffect(() => {
    const audio = audioRefGen.current;

    return () => {
      if (audioUrl?.startsWith("blob:")) URL.revokeObjectURL(audioUrl);
      audio.pause();
    };
  }, [audioUrl]);

  // --- Helpers ---

  const getLanguageName = (code) => {
    const langs = { "en-us": "English (US)", "en-uk": "English (UK)", es: "Spanish", fr: "French", de: "German", it: "Italian", pt: "Portuguese", pl: "Polish", nl: "Dutch" };
    return langs[code] || code;
  };

  const getCharacterGender = (char) => (char?.attributes?.gender || char?.gender || "").toLowerCase();

  const getCharacterLanguage = (char) => (char?.attributes?.language || char?.language || "").toLowerCase();


  const carouselCharacterIds = location.state?.carouselCharacterIds;

  const visibleCharacters = useMemo(() => {
    if (!allCharacters || allCharacters.length === 0) return [];
    if (!selectedCharacter || !prioritizeReturnedCharacter) return allCharacters.slice(0, 5);
    const selected = allCharacters.find((c) => c?._id === selectedCharacter);
    if (!selected) return allCharacters.slice(0, 5);
    const previousCarouselIds = carouselCharacterIds || [];
    const orderedCharacters = previousCarouselIds.length
      ? [
        ...previousCarouselIds
          .map((id) => allCharacters.find((character) => character?._id === id))
          .filter(Boolean),
        ...allCharacters.filter((character) => !previousCarouselIds.includes(character?._id)),
      ]
      : allCharacters;
    const otherCharacters = orderedCharacters.filter((character) => character?._id !== selectedCharacter);
    return [selected, ...otherCharacters.slice(0, 4)];
  }, [allCharacters, carouselCharacterIds, prioritizeReturnedCharacter, selectedCharacter]);

  const isUserFiltering = useMemo(() => {
    return Boolean(
      voiceFilterCategory !== "all" ||
      voiceFilterAge !== "all" ||
      voiceFilterGender !== "all" ||
      voiceSearchQuery
    );
  }, [voiceFilterCategory, voiceFilterAge, voiceFilterGender, voiceSearchQuery]);

  const mergedVoices = useMemo(() => {
    const base = isUserFiltering
      ? mergeUniqueVoices(accumulatedVoices)
      : mergeUniqueVoices(searchedVoices, accumulatedVoices);

    if (voiceSearchQuery || !librarySelectedVoiceDetails) return base;

    return mergeUniqueVoices([librarySelectedVoiceDetails], base);
  }, [isUserFiltering, searchedVoices, accumulatedVoices, librarySelectedVoiceDetails, voiceSearchQuery]);

  const visibleVoices = useMemo(() => {
    return mergeUniqueVoices(
      librarySelectedVoiceDetails ? [librarySelectedVoiceDetails] : [],
      mergedVoices
    ).slice(0, 5);
  }, [mergedVoices, librarySelectedVoiceDetails]);

  // --- Handlers ---



  const handleSelectFromAssetLibrary = (assets) => {
    if (assets && assets.length > 0) {
      const newImages = assets.map(asset => ({
        id: asset._id || asset.id || Math.random().toString(36),
        url: asset.url,
        preview: asset.url,
        isFromLibrary: true
      }));
      setSelectedImages(prev => [...prev, ...newImages]);
      setShowAssetLibrary(false);
    }
  };

  useEffect(() => {
    const audio = previewAudioRef.current;
    const handleEnded = () => setPreviewingVoiceId(null);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleEnded);
    return () => {
      audio.pause();
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleEnded);
    };
  }, []);

  const handlePlayVoice = (voiceId, url) => {
    if (!url) return;
    audioRefGen.current.pause();
    const audio = previewAudioRef.current;
    if (previewingVoiceId === voiceId) {
      audio.pause();
      setPreviewingVoiceId(null);
    } else {
      audio.src = url;
      audio.play().catch(() => setPreviewingVoiceId(null));
      setPreviewingVoiceId(voiceId);
    }
  };

  const handleVoiceSelection = (voiceId, voice, options = {}) => {
    if (voiceId !== selectedVoice) {
      audioRefGen.current.pause();
      setAudioUrl(null);
      setAudioBlob(null);
    }
    setSelectedVoice(voiceId);
    if (voice && options.fromVoiceLibrary) {
      setLibrarySelectedVoiceDetails(voice);
    }
  };

  const insertVoiceCue = (cue) => {
    const before = script.substring(0, cursorPosition);
    const after = script.substring(cursorPosition);
    setScript(before + ` ${cue} ` + after);
  };

  const generateAudio = async () => {
    if (audioRequestRef.current || audioInProcess) return;
    if (!script.trim()) {
      toastify("error", "Enter a script before generating audio.");
      return;
    }

    if (!selectedVoice) {
      toastify("error", "Choose a voice before generating audio.");
      return;
    }
    const controller = new AbortController();
    audioRequestRef.current = controller;
    setAudioInProcess(true);
    audioRefGen.current.pause();
    previewAudioRef.current.pause();
    setPreviewingVoiceId(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${process.env.REACT_APP_API}voices/generate-audio`, {
        voice_id: selectedVoice,
        text: script,
        voice_settings: { stability: voiceStability, speed: voiceSpeed, tone: voiceTone },
      }, { responseType: "blob", signal: controller.signal, headers: { Authorization: `Bearer ${token}` } });

      if (controller.signal.aborted) return;

      const blob = res.data;
      if (!blob?.size) throw new Error("No audio was returned. Please try again.");
      setAudioBlob(blob);
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      audioRefGen.current.src = url;
      audioRefGen.current.load();
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error("Failed to Generate speech:", err);
      let errorMsg = "Failed to Generate speech.";
      if (err?.response?.data instanceof Blob) {
        try {
          const responseText = await err.response.data.text();
          const parsed = JSON.parse(responseText);
          if (parsed?.message) errorMsg = parsed.message;
        } catch (_) { }
      } else if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }

      if (errorMsg === "text is required") {
        errorMsg = "Enter a script before generating audio.";
      } else if (errorMsg === "voice_id is required") {
        errorMsg = "Choose a voice before generating audio.";
      }
      toastify("error", errorMsg);
    } finally {
      if (audioRequestRef.current === controller) {
        audioRequestRef.current = null;
        setAudioInProcess(false);
      }
    }
  };

  const handleGenerateScript = async () => {
    if (isGeneratingScript || !aiCmoId || !conceptId) return;
    setIsGeneratingScript(true);
    try {
      const res = await services.generateScriptFromConcept({
        aiCmoId: aiCmoId,
        conceptId: conceptId,
        voice_settings: { tone: voiceTone },
        instructions: scriptPrompt
      });
      if (res?.data) {
        addGeneratedScript(res.data.script || res.data.generatedScriptText || (typeof res.data === 'string' ? res.data : ""));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsGeneratingScript(false);
    }
  };

  const generateVideoAd = async () => {
    const brandId = localStorage.getItem("selectedBrandId");
    const char = allCharacters.find(c => c._id === selectedCharacter);
    const formData = new FormData();
    formData.append("characterId", char?._id);
    formData.append("voiceId", selectedVoice);
    formData.append("scriptText", script);
    formData.append("video_title", projectName || "Created with Zimzee AI");
    formData.append("brandId", brandId);
    formData.append("voiceSettings", JSON.stringify({
      stability: voiceStability,
      speed: voiceSpeed,
    }));
    if (aiCmoId) formData.append("cmoId", aiCmoId);
    if (conceptId) formData.append("conceptId", conceptId);

    // Use the exact variation shown in the right-side preview.
    if (characterImage) formData.append("imageUrl", characterImage);
    const selectedVariantId = getEntityId(selectedGenerationImage);
    if (selectedVariantId) formData.append("variantId", selectedVariantId);

    if (audioBlob) formData.append("audioFile", audioBlob, "audio.mp3");
    else if (audioUrl) {
      const res = await axios.get(audioUrl, { responseType: 'blob' });
      formData.append("audioFile", res.data, "audio.mp3");
    }

    try {
      await services.generateVideo(formData);
      if (onGenerate) onGenerate();
      else navigate("/app/ads", { state: { tab: "video" } });
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 402) setShowExtraCredits(true);
    }
  };

  const handleShowHistory = async () => {
    setShowGenerationHistory(true);
    if (selectedCharacter) {
      setIsLoadingHistory(true);
      try {
        const res = await services.getCharacterById(selectedCharacter);
        if (res?.data?.variants) setHistoryImages(res.data.variants);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingHistory(false);
      }
    }
  };



  const handleRemoveImage = (id) => {
    setSelectedImages(prev => prev.filter(img => img.id !== id));
  };

  const handleAddImageClick = () => {
    setShowAssetLibrary(true);
  };

  const createPreview = async () => {
    previewRequestTokenRef.current += 1;
    const requestToken = previewRequestTokenRef.current;
    if (previewPollIntervalRef.current) {
      clearInterval(previewPollIntervalRef.current);
      previewPollIntervalRef.current = null;
    }

    const sourceVariant = selectedGenerationImage;
    let editedCharacterImg = sourceVariant?.originalUrl || characterImage || currentChar?.baseImage?.originalUrl || null;

    setIsProcessing(true);
    let uploadedImageUrls = [];

    if (editedCharacterImg) {
      uploadedImageUrls.push(editedCharacterImg);
    }

    if (selectedImages.length > 0) {
      try {
        // Upload new assets or use library URLs
        for (const image of selectedImages) {
          if (image.isFromLibrary || image.url) {
            uploadedImageUrls.push(image.url);
          } else {
            const formData = new FormData();
            formData.append("file", image.file);
            const uploadResponse = await services.uploadImage(formData);
            if (uploadResponse?.statusCode === 201 || uploadResponse?.statusCode === 200 || uploadResponse?.data?.url) {
              uploadedImageUrls.push(uploadResponse.data.url);
            }
          }
        }
      } catch (error) {
        console.error("Error uploading images:", error);
        setIsProcessing(false);
        return;
      }
    }

    let res = null;
    let body = {};

    body = {
      editPrompt: customPrompt,
      referenceImageUrls: uploadedImageUrls,
    };
    if (editedCharacterImg) {
      body.imageUrl = editedCharacterImg;
    }
    const sourceVariantId = getEntityId(sourceVariant);
    if (sourceVariantId) {
      body.variantId = sourceVariantId;
    }

    try {
      res = await services.editCharacter(selectedCharacter, body);
      if (previewRequestTokenRef.current !== requestToken) return;
      const newVariantId = getEntityId(res?.data?.variantId || res?.data?._id);

      let checkCount = 0;
      const maxChecks = 100;

      const checkStatus = async () => {
        try {
          checkCount++;
          const resImage = await services.getCharacterById(selectedCharacter);
          if (previewRequestTokenRef.current !== requestToken) return;
          const variants = resImage?.data?.variants;
          const currentStatus = resImage?.data?.status;

          let isVariantFinished = false;

          if (newVariantId && variants) {
            const targetVariant = variants.find(
              (variant) =>
                String(getEntityId(variant)) === String(newVariantId) ||
                String(getEntityId(variant?.variantId)) === String(newVariantId)
            );

            if (targetVariant) {
              if (targetVariant.status === "active" && targetVariant.originalUrl) {
                setCharacterImage(targetVariant.originalUrl);
                setSelectedGenerationImage(targetVariant);
                isVariantFinished = true;
              } else if (targetVariant.status !== "editing" && targetVariant.status !== "processing") {
                isVariantFinished = Boolean(targetVariant.originalUrl);
              }
            }
          }

          // Fallback
          if (!isVariantFinished && (!newVariantId || !variants)) {
            if (currentStatus !== "editing" && currentStatus !== "processing") {
              if (variants && variants.length > 0) {
                const fallbackVariant = variants[variants.length - 1];
                setCharacterImage(fallbackVariant?.originalUrl);
                setSelectedGenerationImage(fallbackVariant);
              }
              isVariantFinished = true;
            }
          }

          if (isVariantFinished || checkCount >= maxChecks) {
            if (previewPollIntervalRef.current) {
              clearInterval(previewPollIntervalRef.current);
              previewPollIntervalRef.current = null;
            }
            setIsProcessing(false);
            if (checkCount >= maxChecks) {
              console.error("Character generation timed out. Please check again later.");
            }
          }
        } catch (err) {
          if (previewRequestTokenRef.current !== requestToken) return;
          console.error("Error checking character status:", err);
          if (previewPollIntervalRef.current) {
            clearInterval(previewPollIntervalRef.current);
            previewPollIntervalRef.current = null;
          }
          setIsProcessing(false);
        }
      };

      if (selectedCharacter) {
        previewPollIntervalRef.current = setInterval(checkStatus, 5000);
        checkStatus();
      } else {
        setIsProcessing(false);
      }
    } catch (err) {
      if (previewRequestTokenRef.current !== requestToken) return;
      console.error("Error editing character:", err);
      setIsProcessing(false);
    }
  };


  const toggleCarouselFavorite = (id, e) => {
    e.stopPropagation();
    setFavoritedCarouselCharacters(prev => {
      const next = prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id];
      localStorage.setItem("favoriteCharacters", JSON.stringify(next.filter(i => typeof i === "number")));
      localStorage.setItem("favoriteCustomCharacters", JSON.stringify(next.filter(i => typeof i === "string")));
      return next;
    });
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col h-full bg-[#FAFAFA] dark:bg-gray-950 overflow-hidden">
      <div className="flex-1 flex overflow-hidden h-full">
        {/* Left Panel */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-4 py-6 lg:px-6 pb-24">
            <div className="mb-8 space-y-1">
              {projectName && (
                <p className="text-xs font-medium uppercase tracking-wider text-orange-500 dark:text-orange-400">
                  {projectName}
                </p>
              )}
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Create Speaking Character
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Customize your character, script, and voice to generate a video
              </p>
              <button
                type="button"
                onClick={handleShowHistory}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-orange-600 dark:border-gray-700 dark:text-orange-400 xl:hidden"
              >
                <Clock className="h-4 w-4" />
                Generation history
              </button>
            </div>

            {/* Step 1: Character & Language */}
            <div className="rounded-2xl p-6 shadow-sm mb-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-semibold flex items-center justify-center">1</span>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Choose Character & Language</h2>
                </div>
                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center gap-2 rounded-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Globe className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    {getLanguageName(selectedLanguage)}
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Menu.Button>
                  <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100">
                    <Menu.Items className="absolute right-0 mt-2 w-56 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-50 max-h-80 overflow-y-auto">
                      <div className="p-1">
                        {Object.entries({ "en-us": "🇺🇸 English (US)", "en-uk": "🇬🇧 English (UK)", es: "🇪🇸 Spanish", fr: "🇫🇷 French", de: "🇩🇪 German", it: "🇮🇹 Italian", pt: "🇵🇹 Portuguese", pl: "🇵🇱 Polish", nl: "🇳🇱 Dutch" }).map(([code, label]) => (
                          <Menu.Item key={code}>
                            {({ active }) => (
                              <button onClick={() => setSelectedLanguage(code)} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${active ? "bg-gray-100 dark:bg-gray-700" : ""} ${selectedLanguage === code ? "text-orange-500 font-medium" : "text-gray-900 dark:text-white"}`}>
                                {label}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>

              {libraryCharacter && (
                <div className="mb-4 p-3 rounded-xl border border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 flex items-center gap-3">
                  <img src={libraryCharacter.image} alt={libraryCharacter.name} className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{libraryCharacter.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{libraryCharacter.age} · From Character Library</div>
                  </div>
                  <button onClick={() => { setLibraryCharacter(null); handleCharacterSelect(allCharacters[0]); }} className="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-white">Change</button>
                </div>
              )}

              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {visibleCharacters.map((char, idx) => {
                  const isSelected = selectedCharacter === char?._id && !libraryCharacter;
                  const isFavorited = favoritedCarouselCharacters.includes(char?._id);
                  const hideClass = idx === 4 ? "hidden lg:block" : idx === 3 ? "hidden md:block" : idx === 2 ? "hidden sm:block" : "";
                  return (
                    <div key={char?._id} className={`relative group ${hideClass}`}>
                      <button onClick={() => handleCharacterSelect(char)} className={`w-full rounded-xl transition-all ${isSelected ? "ring-2 ring-orange-500" : "hover:scale-105"}`}>
                        <div className="aspect-[9/16] relative rounded-xl overflow-hidden">
                          <img src={char?.baseImage?.originalUrl} alt={char?.name} className="w-full h-full object-cover" />
                          {/* <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" /> */}
                          <div className="absolute bottom-0 left-0 right-0 p-2 flex flex-col">
                            {(char?.isCustom || char?.visibility === "private") && (
                              <span className="self-start px-1.5 py-0.5 bg-neutral-700 text-white text-[9px] font-semibold rounded uppercase mb-1">
                                Custom
                              </span>
                            )}
                            <p className="text-white font-semibold text-xs truncate text-center">{char?.name}</p>
                          </div>
                          {isSelected && <div className="absolute top-2 left-2 w-5 h-5 rounded-md flex items-center justify-center z-10"><Check className="h-3 w-3 text-white" /></div>}
                        </div>
                      </button>
                      <button onClick={(e) => toggleCarouselFavorite(char?._id, e)} className={`absolute top-2 right-2 w-6 h-6 rounded-lg flex items-center justify-center transition-all z-10 ${isFavorited ? "bg-orange-500 opacity-100" : "bg-black/40 opacity-0 group-hover:opacity-100"}`}>
                        <Heart className={`w-3 h-3 ${isFavorited ? "text-white fill-white" : "text-white"}`} />
                      </button>
                    </div>
                  );
                })}
                <button onClick={() => navigate("/app/characters", { state: { returnTo: location.pathname, characterSelectionSource: "all-characters", carouselCharacterIds: visibleCharacters.map((character) => character?._id).filter(Boolean), script, selectedVoice, selectedLanguage, stability: voiceStability, speed: voiceSpeed, customPrompt, brandId: localStorage.getItem("selectedBrandId"), sourceCharacterGender: getCharacterGender(currentChar), audioBlob, audioUrl: audioUrl?.startsWith("http") ? audioUrl : undefined } })}>
                  <div className="aspect-[9/16] rounded-xl border-2 border-dashed border-orange-400 dark:border-orange-700 flex flex-col items-center justify-center gap-2 hover:border-orange-500 transition-all">
                    <span className="text-orange-500 text-xs font-medium text-center leading-tight">View all<br />characters</span>
                    <ChevronRight className="w-4 h-4 text-orange-500" />
                  </div>
                </button>
              </div>
            </div>

            {/* Step 2: Scene Customization */}
            <div className="rounded-2xl p-6 shadow-sm mb-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-semibold flex items-center justify-center">2</span>
                <h2 className="min-w-0 flex-1 text-base font-semibold text-gray-900 dark:text-white">Customize Scene</h2>
                <span className="shrink-0 whitespace-nowrap px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">Optional</span>
              </div>
              <div className="flex flex-col gap-3 lg:flex-row">
                <div className="flex-1 min-w-0 w-full">
                  <textarea
                    placeholder="E.g., Stand in Times Square, wear a red shirt..."
                    rows={4}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 resize-none text-sm leading-relaxed focus:outline-none focus:border-orange-300"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                  />
                  {selectedImages.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3">
                      {selectedImages.map(img => (
                        <div key={img.id} className="relative group">
                          <img src={img.preview} alt="Preview" className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200 dark:border-white/10" />
                          <button onClick={() => handleRemoveImage(img.id)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex w-full flex-col gap-2 lg:w-auto lg:shrink-0">
                  <button onClick={createPreview} disabled={isProcessing} className="flex w-full items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-4 h-10 text-sm font-medium transition-colors disabled:opacity-50">
                    {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    {isProcessing ? "Processing..." : "Update Scene"}
                  </button>

                  <button onClick={handleAddImageClick} className="flex w-full items-center gap-2 justify-center border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-full px-4 h-10 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <Image className="w-4 h-4" /> Add image elements
                  </button>
                </div>
              </div>
            </div>

            {/* Step 3: Voice & Script */}
            <div className="rounded-2xl p-4 sm:p-6 shadow-sm mb-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-semibold flex items-center justify-center">3</span>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">What will they say?</h2>
              </div>

              {/* Voice Selection */}
              <div className="grid grid-cols-2 gap-2 mb-4 min-h-[44px] sm:flex sm:flex-wrap sm:items-center">
                {loadingVoices && accumulatedVoices.length === 0 ? (
                  <div className="col-span-2 flex items-center gap-3 px-4 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading voices...</span>
                  </div>
                ) : (
                  <>
                    {visibleVoices.map((voice) => {
                      const isSelected = selectedVoice === voice.voiceId;
                      const displayName = voice.name.length > 12 ? voice.name.substring(0, 10) + '..' : voice.name;
                      return (
                        <div
                          key={voice.voiceId}
                          onClick={() => handleVoiceSelection(voice.voiceId, voice)}
                          className={`flex w-full min-w-0 items-center gap-1.5 px-2 py-1.5 sm:w-auto sm:gap-2 sm:px-3 sm:py-2 rounded-full border transition-all cursor-pointer ${isSelected ? "border-orange-500 bg-orange-50 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400" : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600"}`}
                        >
                          <button onClick={(e) => { e.stopPropagation(); handlePlayVoice(voice.voiceId, voice.previewUrl); }} className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center ${previewingVoiceId === voice.voiceId ? "bg-orange-500 text-white animate-pulse" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300"}`}>
                            {previewingVoiceId === voice.voiceId ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
                          </button>
                          <span className="min-w-0 truncate text-xs sm:text-sm font-medium" title={voice.name}>{displayName}</span>
                        </div>
                      );
                    })}
                    <button onClick={() => setShowVoiceLibrary(true)} className="col-span-2 justify-self-start text-sm font-medium text-orange-500 hover:text-orange-600 px-3 py-2">View all voices</button>
                  </>
                )}
              </div>

              {/* Voice Controls */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Menu as="div" className="relative">
                  <Menu.Button className="h-9 px-4 rounded-full border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <Brackets className="w-4 h-4" /> Insert Voice Cues <ChevronDown className="w-4 h-4" />
                  </Menu.Button>
                  <Transition as={Fragment} enter="transition duration-100" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100">
                    <Menu.Items className="absolute left-0 mt-2 w-48 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-50 p-1 max-h-60 overflow-y-auto">
                      {voiceCues.map((cue) => (
                        <Menu.Item key={cue.value}>
                          {({ active }) => (
                            <button onClick={() => insertVoiceCue(cue.value)} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center text-gray-700 dark:text-gray-300 ${active ? "bg-gray-100 dark:bg-gray-700" : ""}`}>{cue.icon} {cue.label}</button>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>

                <button ref={voiceSettingsBtnRef} onClick={() => setShowVoiceSettings(!showVoiceSettings)} className={`h-9 px-4 rounded-full border text-sm font-medium flex items-center gap-2 transition-all ${showVoiceSettings ? "bg-orange-50 dark:bg-orange-500/20 border-orange-500 text-orange-600 dark:text-orange-400" : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"}`}>
                  <Settings className="w-4 h-4" /> Voice Settings
                </button>
              </div>

              {showVoiceSettings && (
                <div ref={voiceSettingsRef} className="mb-4 min-w-0 p-3 sm:p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 space-y-4">

                  <div>
                    <div className="mb-2 text-left">
                      <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">Delivery Style</label>
                    </div>
                    <VoiceStabilityControl
                      value={voiceStability}
                      onChange={setVoiceStability}
                    />
                  </div>
                </div>
              )}

              {/* Script Generation */}
              <div className="mb-3 flex flex-wrap items-center gap-2 sm:gap-3">
                <button
                  onClick={handleGenerateScript}
                  disabled={isGeneratingScript}
                  className="flex w-full sm:w-auto min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                >
                  {isGeneratingScript ? <Loader2 className="w-4 h-4 shrink-0 animate-spin" /> : <Sparkles className="w-4 h-4 shrink-0" />}
                  {isGeneratingScript ? "Generating..." : "Generate new script"}
                </button>

                <button
                  disabled
                  className="flex w-full sm:w-auto min-h-10 items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all text-gray-500 dark:text-gray-400 opacity-50 cursor-not-allowed"
                >
                  {showScriptPrompt ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  Add Instructions
                </button>
                <ScriptHistoryControls
                  currentScriptNumber={currentScriptNumber}
                  scriptCount={scriptCount}
                  goToPreviousScript={goToPreviousScript}
                  goToNextScript={goToNextScript}
                  disabled={isGeneratingScript}
                />
              </div>

              {showScriptPrompt && (
                <div className="mb-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <textarea
                    value={scriptPrompt}
                    onChange={e => setScriptPrompt(e.target.value)}
                    placeholder="E.g., Highlight features, keep it professional..."
                    className="w-full min-h-[80px] max-h-[150px] overflow-y-auto hide-scrollbar bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white p-3 rounded-lg text-sm resize-y focus:outline-none focus:border-orange-500 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              )}

              <textarea
                ref={scriptTextareaRef}
                value={script}
                onChange={e => {
                  setScript(e.target.value);
                  setCursorPosition(e.target.selectionStart);
                }}
                onSelect={e => setCursorPosition(e.target.selectionStart)}
                placeholder="Write your script here..."
                className="w-full min-h-[120px] max-h-[400px] p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm resize-y focus:outline-none focus:border-orange-500 mb-4 overflow-y-auto hide-scrollbar leading-relaxed placeholder-gray-400 dark:placeholder-gray-500"
              />

              <div className="flex flex-wrap items-center gap-4">
                {/* Word and second count */}
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                    {script.trim().split(/\s+/).filter(Boolean).length} words
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <span className="text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                    ~{Math.ceil(script.trim().split(/\s+/).filter(Boolean).length / 2.5)} sec
                  </span>
                </div>

                <button type="button" onClick={generateAudio} disabled={audioInProcess} aria-busy={audioInProcess} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5 h-10 shrink-0 text-sm font-medium flex items-center disabled:opacity-50">
                  {audioInProcess ? <Loader2 aria-hidden="true" className="w-4 h-4 mr-1 animate-spin motion-reduce:animate-none" /> : <RefreshCw aria-hidden="true" className="w-4 h-4 mr-1" />}
                  Generate speech
                </button>
                {(audioUrl || audioInProcess) && (
                  <SpeechWaveformPlayer
                    audioUrl={audioUrl}
                    audioBlob={audioBlob}
                    audioRef={audioRefGen}
                    isGenerating={audioInProcess}
                    onBeforePlay={() => {
                      previewAudioRef.current.pause();
                      setPreviewingVoiceId(null);
                    }}
                  />
                )}
              </div>
            </div>

            {/* Create Video CTA */}
            <div className="flex flex-wrap items-center justify-between gap-4 mt-10">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <Sparkles className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium">
                  Credits needed: {(() => {
                    const words = script.trim().split(/\s+/).filter(Boolean).length;
                    const estimatedDuration = Math.ceil(words / 2.5);
                    return words > 0 ? (estimatedDuration > 60 ? 750 : 500) : 0;
                  })()}
                </span>
              </div>
              <button
                onClick={generateVideoAd}
                disabled={loadingVideo || isProcessing || audioInProcess || !selectedCharacter || !selectedVoice || !script.trim() || !audioUrl}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-full px-8 h-12 text-base font-semibold shadow-lg shadow-orange-200 dark:shadow-orange-500/20 transition-all flex items-center justify-center gap-2"
              >
                {loadingVideo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
                <span>{loadingVideo ? "Generating Video..." : "Create Final Video"}</span>
              </button>
            </div>
            <p className="ai-disclaimer mt-6">
              <span className="ai-disclaimer__lead">AI can make mistakes.</span>{" "}
              Review your content before publishing. Claims, personal experiences, and testimonials must be genuine and accurate.
            </p>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-[380px] shrink-0 overflow-y-auto bg-white dark:bg-[#111111] border-l border-gray-100 dark:border-gray-800 hidden xl:block">
          <div className="p-6 pb-24">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Preview</h3>
              <button onClick={handleShowHistory} className="text-sm font-medium text-orange-500 flex items-center gap-1.5 hover:text-orange-600">
                <Clock className="w-4 h-4" /> History
              </button>
            </div>
            <div
              className={`rounded-2xl overflow-hidden relative aspect-[9/16] ${
                isProcessing ? "bg-white dark:bg-white/5" : "bg-gray-100 dark:bg-gray-800"
              }`}
            >
              {currentChar ? (
                <>
                  {isProcessing ? (
                    <div className="absolute inset-0 animate-pulse bg-gray-100 dark:bg-white/5 z-10">
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                        <div className="w-10 h-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin"></div>
                        <p className="text-xs font-medium animate-pulse text-orange-500 dark:text-orange-400">Generating Character...</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <img
                        src={characterImage || currentChar?.baseImage?.originalUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (characterImage) {
                            console.warn("Character image failed to load, falling back to base image");
                            setCharacterImage(null);
                            setSelectedGenerationImage(null);
                          }
                        }}
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Character</span>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">Select a character</div>
              )}
            </div>

            {/* Collapsible Tips */}
            <Disclosure>
              {({ open }) => (
                <div className="mt-6">
                  <Disclosure.Button className={`w-full p-4 ${open ? "rounded-t-xl border-b-0" : "rounded-xl"} bg-orange-50 dark:bg-orange-950/50 border border-orange-100 dark:border-orange-900/50 hover:bg-orange-100/70 dark:hover:bg-orange-950/70 transition-colors block text-left`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-orange-500" />
                        <h4 className="font-semibold text-sm text-gray-900 dark:text-orange-200">How to Make Your Videos Feel Real</h4>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-orange-500 dark:text-orange-300 transition-transform ${open ? "rotate-180" : ""}`} />
                    </div>
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-4 pb-4 pt-4 bg-orange-50 dark:bg-orange-950/50 border border-t-0 border-orange-100 dark:border-orange-900/50 rounded-b-xl">
                    <div className="space-y-5 text-xs text-gray-600 dark:text-orange-200/80">

                      <p className="text-sm text-gray-700 dark:text-orange-200">These tips help your AI videos sound and feel more natural, believable, and human.</p>

                      <p className="text-xs italic text-gray-500 dark:text-orange-300">Keep scripts under 60 seconds for best engagement</p>

                      {/* Natural Speech */}
                      <div>
                        <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Natural Speech</h5>
                        <ul className="space-y-1.5">
                          <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>Write the way people actually talk — avoid formal marketing language</span></li>
                          <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>Keep sentences short and conversational</span></li>
                          <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>{"Avoid overusing [pause] or … — it can feel unnatural"}</span></li>
                        </ul>
                        <div className="mt-3 p-2.5 rounded-lg space-y-1.5 bg-white dark:bg-gray-900/50">
                          <p className="text-xs text-gray-500 dark:text-gray-400"><span className="font-medium">Instead of:</span> This revolutionary platform dramatically improves productivity.</p>
                          <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-medium text-orange-500">Try:</span> I tried this and honestly it worked way better than I expected.</p>
                        </div>
                      </div>

                      {/* Natural Pacing */}
                      <div>
                        <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Natural Pacing</h5>
                        <ul className="space-y-1.5">
                          <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>Slightly faster speech sounds more natural — real people talk quickly</span></li>
                          <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>{"Use [fast] or [faster] for casual, excited delivery"}</span></li>
                          <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>{"Use [slow] only for important emphasis"}</span></li>
                        </ul>
                      </div>

                      {/* Voice Cues */}
                      <div>
                        <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Voice Cues</h5>
                        <div className="p-2.5 rounded-lg grid grid-cols-2 gap-x-4 gap-y-1 bg-white dark:bg-gray-900/50">
                          <p className="text-xs"><span className="font-mono text-orange-500">[fast]</span> faster delivery</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[faster]</span> very quick, excited</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[slow]</span> slower emphasis</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[pause]</span> natural break</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[laugh]</span> light laugh</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[chuckle]</span> soft laugh</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[giggles]</span> playful laughter</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[sigh]</span> relief/frustration</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[whisper]</span> quieter tone</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[breath]</span> breathing pause</p>
                          <p className="text-xs"><span className="font-mono text-orange-500">[emphasis]</span> stress words</p>
                        </div>
                        <p className="text-xs mt-2 italic text-gray-500 dark:text-gray-400">{"Example: I almost didn't try this… [chuckle] but I'm glad I did."}</p>
                      </div>

                      {/* Emotion & Punctuation */}
                      <div>
                        <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Emotion & Punctuation</h5>
                        <ul className="space-y-1">
                          <li className="flex items-center gap-2"><span className="text-orange-500">•</span> <span><strong>!</strong> adds excitement</span></li>
                          <li className="flex items-center gap-2"><span className="text-orange-500">•</span> <span><strong>?</strong> creates curiosity</span></li>
                          <li className="flex items-center gap-2"><span className="text-orange-500">•</span> <span><strong>…</strong> creates a thinking pause</span></li>
                        </ul>
                      </div>

                      {/* Setting & Style */}
                      <div>
                        <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Setting & Style</h5>
                        <p className="text-xs mb-2 text-gray-400 dark:text-orange-300/60">Casual settings feel more authentic. Good options:</p>
                        <ul className="space-y-1">
                          <li className="flex items-center gap-2"><span className="text-orange-500">•</span> home office, kitchen, living room</li>
                          <li className="flex items-center gap-2"><span className="text-orange-500">•</span> natural window light or soft indoor lighting</li>
                          <li className="flex items-center gap-2"><span className="text-orange-500">•</span> talk like sharing with a friend, not reading a script</li>
                        </ul>
                      </div>

                      {/* Natural Hooks */}
                      <div>
                        <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Natural Hooks</h5>
                        <p className="text-xs mb-2 text-gray-400 dark:text-orange-300/60">Casual openers that grab attention:</p>
                        <div className="p-2.5 rounded-lg bg-white dark:bg-gray-900/50">
                          <p className="text-xs italic text-gray-600 dark:text-gray-300">{"\"I didn't expect this to work…\" • \"I wish I knew this earlier.\" • \"Nobody talks about this.\""}</p>
                        </div>
                      </div>

                    </div>
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
          </div>
        </div>
      </div>

      {/* Sidebars */}
      <VoiceLibrarySidebar
        showVoiceLibrary={showVoiceLibrary}
        setShowVoiceLibrary={setShowVoiceLibrary}
        selectedVoice={selectedVoice}
        setSelectedVoice={handleVoiceSelection}
        voicesData={mergedVoices}
        voiceSearchQuery={voiceSearchQuery}
        setVoiceSearchQuery={setVoiceSearchQuery}
        voiceFilterGender={voiceFilterGender}
        setVoiceFilterGender={setVoiceFilterGender}
        voiceFilterAge={voiceFilterAge}
        setVoiceFilterAge={setVoiceFilterAge}
        voiceFilterCategory={voiceFilterCategory}
        setVoiceFilterCategory={setVoiceFilterCategory}
        loadMoreVoices={() => { }} // Handle pagination if needed
        hasMoreVoices={hasMoreVoices}
        isLoadingMore={isLoadingMore}
        loadingVoices={loadingVoices || isSyncingVoices}
        voicePage={voicePage}
        prioritizedVoiceId={librarySelectedVoiceDetails?.voiceId}
      />

      {showGenerationHistory && (
        <GenerationHistorySidebar
          showGenerationHistory={showGenerationHistory}
          setShowGenerationHistory={setShowGenerationHistory}
          generatedImages={historyImages}
          selectedGenerationImage={selectedGenerationImage}
          loading={isLoadingHistory}
          setSelectedGenerationImage={setSelectedGenerationImage}
          setCharacterImage={setCharacterImage}
        />
      )}
      <ExtraCreditsModal
        isOpen={showExtraCredits}
        onClose={() => setShowExtraCredits(false)}
      />
      <AdAssetLibraryModal
        isOpen={showAssetLibrary}
        onClose={() => setShowAssetLibrary(false)}
        onSelect={handleSelectFromAssetLibrary}
        allowedTypes={['image']}
      />
    </div>
  );
}
